import React, { FC, ReactElement, useRef, useState } from 'react';
import { HorizontalDragUniversal } from '@duxton-web/icons';
import {
  Button,
  Form,
  Input,
  notification,
  Select,
  Typography,
} from '@duxton-web/ui';

import Service, { UpdateStateResponse } from '../../lib/App';

import './PausePrompt.css';

import { FormLayout } from '@duxton-web/ui/es/form/Form';

import { HTTP_STATUS } from '@/constants';

const notificationApi = notification;

interface PauseType {
  value: string;
  text: string;
  reasonRequired: boolean;
}

export const pauseTypes: PauseType[] = [
  {
    value: 'notWorking',
    text: 'I am pausing as moderator because the Experiment configuration is incorrect.',
    reasonRequired: true,
  },
  {
    value: 'facetNotInstrumented',
    text: 'I am pausing as moderator because one or more facets used in the experiment were not instrumented.',
    reasonRequired: true,
  },
  {
    value: 'interference',
    text: 'I am pausing as overlapping experiment owner because this experiment has too much inferences with my overlapping experiment.',
    reasonRequired: false,
  },
  {
    value: 'other',
    text: 'Other reason',
    reasonRequired: true,
  },
];

interface PausePromptProps {
  experiment: string;
  isAdaptiveExp?: boolean;
  layout?: FormLayout;
  onCancel?: () => void;
  onSuccess?: () => void;
}

const PausePrompt: FC<PausePromptProps> = ({
  experiment,
  isAdaptiveExp,
  layout,
  onCancel,
  onSuccess,
}) => {
  const [pauseType, setPauseType] = useState<{
    value: string;
    error: boolean;
  }>({
    value: '',
    error: false,
  });
  const [reasonErrorValue, setReasonErrorValue] = useState<string>('');

  const reason = useRef<string>('');
  const pauseTypeObj = pauseTypes.find((rt) => rt.value == pauseType.value);

  const validate = (): boolean => {
    if (!pauseType.value) {
      setPauseType({
        ...pauseType,
        error: true,
      });

      return false;
    }

    if (pauseTypeObj.reasonRequired) {
      if (!reason.current) {
        setReasonErrorValue('This field is required');

        return false;
      }
      const cleanReason = reason.current.trim();
      if (cleanReason.length <= 10) {
        setReasonErrorValue(
          'An ideal reason is usually more than 10 characters long. Please provide a valid reason.',
        );
        return false;
      }
    }
    return true;
  };

  async function pause(): Promise<void> {
    if (!validate()) {
      return;
    }

    try {
      await Service.planner!.changeExperimentState(
        experiment,
        'pause',
        `${pauseType.value}:${reason.current}`,
        isAdaptiveExp,
      ).then((res: UpdateStateResponse) => {
        if ((!isAdaptiveExp && res) || isAdaptiveExp) {
          notificationApi.success({
            message: 'Pausing an Experiment',
            description: 'Experiment was successfully paused.',
          });
          onSuccess();
        }
      });
    } catch (error) {
      let description =
        'Experiment request was not paused, due to: ' + error.toString();
      if (error.response && error.response.status === HTTP_STATUS.FORBIDDEN) {
        description =
          'Experiment request was not paused, due to: not enough permissions';
      }
      notificationApi.error({
        description,
        message: 'Pausing an Experiment',
      });
    }
  }

  function handlePauseType(value: string): void {
    setPauseType({
      ...pauseType,
      value,
      error: false,
    });
  }

  function handleReason(event: React.ChangeEvent<HTMLTextAreaElement>): void {
    setReasonErrorValue('');
    reason.current = event.target.value;
  }

  function renderContent(): ReactElement {
    return (
      <div className="pause-prompt__container">
        <div className="pause-prompt__header">
          <Typography.Text strong type="danger">
            {experiment}
          </Typography.Text>
        </div>
        <Form layout={layout}>
          <Form.Item
            label="Select reason why you are pausing this experiment?"
            required
            validateStatus={!pauseType.error ? 'error' : ''}
            help={!pauseType.error ? 'This field is required' : ''}
          >
            <Select onChange={handlePauseType}>
              {pauseTypes.map((rt) => (
                <Select.Option key={rt.value} value={rt.value}>
                  {rt.text}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="Additional reasons. Please provide a valid reason to help us accelerate Experimentation at Grab."
            required={pauseTypeObj && pauseTypeObj.reasonRequired}
            validateStatus={reasonErrorValue ? 'error' : ''}
            help={reasonErrorValue ? reasonErrorValue : ''}
          >
            <Input.TextArea rows={8} onChange={handleReason} />
          </Form.Item>
          <Form.Item>
            {onCancel && (
              <Button size="small" onClick={onCancel}>
                Back
              </Button>
            )}
            <Button
              size="small"
              danger
              icon={<HorizontalDragUniversal />}
              className="pause-prompt__reject-button"
              onClick={pause}
            >
              Pause
            </Button>
          </Form.Item>
        </Form>
      </div>
    );
  }

  return renderContent();
};

export default PausePrompt;
